import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import InputFormBorder from '../Common/InputFormBorder/InputFormBorder';
import styles from './ModalSelector.module.css';
import Modal from '../Common/Modal/Modal';
import { setCanRefresh } from '../../actions/appActions';
import EmptyActivity from '../Common/EmptyActivity/EmptyActivity';

const ModalSelector = ({
  options,
  selectedValue,
  onChange,
  placeholder,
  disabled = false,
  enableParentReset = false,
  groupedData = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isModalOpen, setModalOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [displayText, setDisplayText] = useState(
    placeholder || (options.length > 0 ? options[0].label : ''),
  );

  const [selectedOption, setSelectedOption] = useState(() => {
    if (!selectedValue) return null;

    if (groupedData) {
      for (const zone of groupedData) {
        const pickup = zone.pickups.find((p) => p.id === selectedValue.value);
        if (pickup) {
          return { label: pickup.name, value: pickup.id, zoneId: zone.zoneId };
        }
      }
      return null;
    } else {
      return options.find((option) => option.value === selectedValue.value) || null;
    }
  });

  const modalContentRef = useRef(null);
  const selectedOptionRef = useRef(null);
  const appCanRefresh = useSelector((state) => state.app.canRefresh);

  const opacity = disabled ? 0.5 : 1;
  const textClassName = selectedOption ? styles.selectorText : styles.placeholderText;

  useEffect(() => {
    if (!enableParentReset) return;

    if (!selectedValue) {
      setSelectedOption(null);
      setDisplayText(placeholder || '');
    } else {
      let option = null;
      if (groupedData) {
        for (const zone of groupedData) {
          const pickup = zone.pickups.find((p) => p.id === selectedValue.value);
          if (pickup) {
            option = { label: pickup.name, value: pickup.id, zoneId: zone.zoneId };
            break;
          }
        }
      } else {
        option = options.find((opt) => opt.value === selectedValue.value);
      }

      setSelectedOption(option);
      setDisplayText(option ? option.label : placeholder || '');
    }
  }, [selectedValue, options, groupedData, placeholder, enableParentReset]);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : 'auto';
    return () => (document.body.style.overflow = 'auto');
  }, [isModalOpen]);

  const normalizeText = (text) => {
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '');
  };

  const filteredOptions = React.useMemo(() => {
    if (groupedData) {
      if (!filter.trim()) return groupedData;
      const normalizedFilter = normalizeText(filter);

      return groupedData
        .map((zone) => ({
          ...zone,
          pickups: zone.pickups.filter((pickup) =>
            normalizeText(pickup.name).includes(normalizedFilter),
          ),
        }))
        .filter((zone) => zone.pickups.length > 0);
    } else {
      if (!filter.trim()) return options;
      const normalizedFilter = normalizeText(filter);
      return options.filter((option) => normalizeText(option.label).includes(normalizedFilter));
    }
  }, [options, groupedData, filter]);

  const handleSearchInputChange = (e) => {
    setFilter(e.target.value);
  };

  const handleOpenModal = () => {
    if (!disabled) {
      setModalOpen(true);
      setFilter('');
    }
  };

  useLayoutEffect(() => {
    if (isModalOpen && selectedOptionRef.current) {
      requestAnimationFrame(() => {
        selectedOptionRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        });
      });
    }
  }, [isModalOpen]);

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSelectPickup = useCallback(
    (pickup) => {
      const option = { label: pickup.name, value: pickup.id, zoneId: pickup.zoneId };
      setSelectedOption(option);
      setDisplayText(pickup.name);
      setFilter('');
      onChange(option);
      setModalOpen(false);
    },
    [onChange],
  );

  const handleSelectOption = useCallback(
    (option) => {
      setSelectedOption(option);
      setDisplayText(option.label);
      setFilter('');
      onChange(option);
      setModalOpen(false);
    },
    [onChange],
  );

  const isOptionSelected = (option) => {
    if (!selectedOption) return false;
    return normalizeText(option.label || option.name) === normalizeText(selectedOption.label);
  };

  const isPickupSelected = (pickup) => {
    if (!selectedOption) return false;
    return pickup.id === selectedOption.value;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
        handleCloseModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (appCanRefresh) {
      dispatch(setCanRefresh(false));
      return () => dispatch(setCanRefresh(true));
    }
  }, []);

  const renderGroupedContent = () => {
    return filteredOptions.map((zone) => (
      <div key={zone.zoneId} className={styles.groupContainer}>
        <div className={styles.zoneHeader}>
          <span className={styles.groupName}>{zone.name}</span>
        </div>
        {zone.pickups.map((pickup) => {
          const selected = isPickupSelected(pickup);
          return (
            <div key={pickup.id}>
              <div
                ref={selected ? selectedOptionRef : null}
                className={`${styles.listItem} ${styles.pickupItem} ${
                  selected ? styles.listItemSelected : ''
                }`}
                onClick={() => handleSelectPickup(pickup)}
              >
                <div className={styles.option}>
                  <span className={styles.countryLabel}>{pickup.name}</span>
                  <div className={styles.checkBoxContainer}>
                    {selected && <span className={styles.checkMark}>✓</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ));
  };

  const renderFlatContent = () => {
    return filteredOptions.map((option) => {
      const selected = isOptionSelected(option);
      return (
        <div key={`${option.value}-${option.label}`}>
          <div
            ref={selected ? selectedOptionRef : null}
            className={`${styles.listItem} ${selected ? styles.listItemSelected : ''}`}
            onClick={() => handleSelectOption(option)}
          >
            <div className={styles.option}>
              <span className={styles.countryLabel}>{option.label}</span>
              <div className={styles.checkBoxContainer}>
                {selected && <span className={styles.checkMark}>✓</span>}
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className={styles.selectorContainer}>
      <div
        className='select-picker'
        onClick={() => handleOpenModal()}
        style={{
          opacity,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <div className={`${textClassName} text-truncate pr-4`}>{displayText}</div>
        <ChevronDown size={18} color='var(--color-primary)' />
      </div>

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        fullScreen={false}
        showCloseButton={true}
        hasBackground={true}
        customStyle={{ height: '60vh' }}
      >
        <div
          className={styles.modalContent}
          ref={modalContentRef}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.searchInput}>
            <InputFormBorder
              name='search'
              type='text'
              placeholder={t('search')}
              value={filter}
              onChange={handleSearchInputChange}
              autoFocus
              required={false}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
            />
          </div>

          <div className={styles.listContainer}>
            {groupedData ? renderGroupedContent() : renderFlatContent()}

            {((groupedData && filteredOptions.every((zone) => zone.pickups.length === 0)) ||
              (!groupedData && filteredOptions.length === 0)) && (
              <div className=' m-3'>
                <EmptyActivity title={t('noMatchesFound')} />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ModalSelector;
